import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Briefcase,
  Building2,
  Check,
  ChevronDown,
  Clock,
  Gift,
  GraduationCap,
  HardHat,
  Heart,
  Laptop,
  Linkedin,
  Mail,
  MapPin,
  Megaphone,
  Phone,
  ShieldCheck,
  Sparkles,
  Trophy,
  TrendingUp,
  UserCheck,
  Users,
  Wallet,
  Watch,
} from "lucide-react";

import { CONTACT_EMAIL } from "@/components/legal-page";
import heroImage from "@/assets/hero-ambassador.jpg";
import hero640Avif from "@/assets/hero-ambassador-640.avif";
import hero960Avif from "@/assets/hero-ambassador-960.avif";
import hero1280Avif from "@/assets/hero-ambassador-1280.avif";
import hero640Webp from "@/assets/hero-ambassador-640.webp";
import hero960Webp from "@/assets/hero-ambassador-960.webp";
import hero1280Webp from "@/assets/hero-ambassador-1280.webp";
import blueprintImage from "@/assets/blueprint-wireframe.jpg";
import bp512Avif from "@/assets/blueprint-wireframe-512.avif";
import bp768Avif from "@/assets/blueprint-wireframe-768.avif";
import bp1024Avif from "@/assets/blueprint-wireframe-1024.avif";
import bp512Webp from "@/assets/blueprint-wireframe-512.webp";
import bp768Webp from "@/assets/blueprint-wireframe-768.webp";
import bp1024Webp from "@/assets/blueprint-wireframe-1024.webp";

import { supabase } from "@/integrations/supabase/client";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export const Route = createFileRoute("/")({
  component: LandingPage,
  head: () => ({
    meta: [
      { title: "Civil Engineering Campus Ambassador Program – Bengaluru" },
      {
        name: "description",
        content:
          "Become a Civil Engineering Campus Ambassador in Bengaluru. Earn ₹500 per successful CAD & BIM license sale, get certified, and build your career.",
      },
      {
        property: "og:title",
        content: "Become a Civil Engineering Campus Ambassador – Bengaluru",
      },
      {
        property: "og:description",
        content:
          "Earn ₹500 per successful license sale, free training, certificate, LinkedIn recommendation and internship opportunities.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/" }],
  }),
});

/* ---------------------------------- motion --------------------------------- */

function Reveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "li";
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${
        visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      } ${className}`}
    >
      {children}
    </Tag>
  );
}


/* ------------------------------- static data ------------------------------- */

const benefits = [
  {
    icon: Wallet,
    title: "₹500",
    lines: ["Commission per", "Successful Sale"],
    tone: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: Award,
    title: "Certificate",
    lines: ["of Achievement"],
    tone: "bg-violet-50 text-violet-600",
  },
  {
    icon: Linkedin,
    title: "LinkedIn",
    lines: ["Recommendation"],
    tone: "bg-sky-50 text-sky-600",
  },
  {
    icon: Briefcase,
    title: "Internship",
    lines: ["Opportunity"],
    tone: "bg-rose-50 text-rose-500",
  },
  {
    icon: Trophy,
    title: "Performance",
    lines: ["Monthly Rewards"],
    tone: "bg-amber-50 text-amber-600",
  },
  {
    icon: Clock,
    title: "Flexible",
    lines: ["Working Hours"],
    tone: "bg-teal-50 text-teal-600",
  },
] as const;

const steps = [
  {
    icon: GraduationCap,
    label: "Apply",
    copy: "Submit your application in under 2 minutes",
    tone: "text-emerald-600 border-emerald-200",
    dot: "bg-emerald-500",
  },
  {
    icon: UserCheck,
    label: "Get Selected",
    copy: "Our team reviews and confirms on WhatsApp",
    tone: "text-sky-600 border-sky-200",
    dot: "bg-sky-500",
  },
  {
    icon: BadgeCheck,
    label: "Free Training",
    copy: "Learn the products, sales and communication",
    tone: "text-violet-600 border-violet-200",
    dot: "bg-violet-500",
  },
  {
    icon: Megaphone,
    label: "Start Promoting",
    copy: "Share with your batch and generate leads",
    tone: "text-amber-600 border-amber-200",
    dot: "bg-amber-500",
  },
  {
    icon: Wallet,
    label: "Earn Commission",
    copy: "₹500 credited for every successful sale",
    tone: "text-emerald-600 border-emerald-200",
    dot: "bg-emerald-500",
  },
] as const;

const eligibility = [
  "Civil Engineering Students",
  "Diploma Civil Students",
  "Final Year Students",
  "Fresh Graduates",
] as const;

const rewards = [
  { icon: Laptop, label: "Laptop" },
  { icon: Watch, label: "Smart Watch" },
  { icon: Gift, label: "Gift Vouchers" },
] as const;

const valueProps = [
  {
    icon: Building2,
    title: "Build Your Career",
    copy: "Gain real-world experience and strengthen your resume",
  },
  {
    icon: Users,
    title: "Grow Your Network",
    copy: "Connect directly with industry professionals in Bengaluru",
  },
  {
    icon: Heart,
    title: "Make an Impact",
    copy: "Help your peers learn software that gets them hired",
  },
  {
    icon: ShieldCheck,
    title: "Earn While You Learn",
    copy: "Turn your campus time into real financial growth",
  },
] as const;

const faqs = [
  {
    q: "Do I need prior sales experience?",
    a: "No. We select students on attitude, not experience. Every selected ambassador gets free training on the software, the pitch and how to talk to students before starting.",
  },
  {
    q: "How much time does this take every week?",
    a: "Most ambassadors spend 4–6 hours a week. You choose your own hours, so it works around lectures, labs and exams.",
  },
  {
    q: "When and how is my commission paid?",
    a: "₹500 is credited for every successful license sale. Payouts are processed to your bank account or UPI within 7 working days of the sale being confirmed.",
  },
  {
    q: "Is this open to students outside Bengaluru?",
    a: "The current batch is focused on Bengaluru colleges. If you're from another city, apply anyway — we'll add you to the waitlist for the next intake.",
  },
] as const;

/* --------------------------------- the page -------------------------------- */

interface FormState {
  fullName: string;
  collegeName: string;
  branch: string;
  year: string;
  whatsapp: string;
  email: string;
}

const emptyForm: FormState = {
  fullName: "",
  collegeName: "",
  branch: "",
  year: "",
  whatsapp: "",
  email: "",
};

const CIVIL_BRANCHES = [
  "Civil Engineering",
  "Structural Engineering",
  "Construction Technology & Management",
  "Transportation Engineering",
  "Environmental Engineering",
  "Geotechnical Engineering",
  "Water Resources Engineering",
  "Surveying & Geomatics",
] as const;


function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main>
        <Hero />
        <BenefitsSection />
        <HowItWorks />
        <ApplySection />
        <ValueStrip />
        <FaqSection />
        <FinalCta />
      </main>
      <SiteFooter />
    </div>
  );
}

function BrandMark() {
  return (
    <a href="#top" className="flex items-center gap-3">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gold text-gold-foreground shadow-lg shadow-black/20">
        <HardHat className="h-5 w-5" />
      </span>
      <span className="leading-tight">
        <span className="block text-sm font-extrabold tracking-[0.18em] text-navy-foreground">
          DESIGNFUTURE
        </span>
        <span className="block text-[10px] font-medium tracking-[0.22em] text-navy-foreground/60">
          CAMPUS AMBASSADOR PROGRAM
        </span>
      </span>
    </a>
  );
}

function SiteHeader() {
  return (
    <header id="top" className="absolute inset-x-0 top-0 z-40">
      <div className="container-tight flex items-center justify-between py-5">
        <BrandMark />
        <div className="hidden items-center gap-2 rounded-full bg-navy/80 px-5 py-2.5 text-xs font-bold tracking-wide text-navy-foreground ring-1 ring-white/10 backdrop-blur md:flex">
          <HardHat className="h-4 w-4 text-gold" />
          FOR CIVIL ENGINEERING STUDENTS ONLY
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-navy pb-20 pt-28 md:pb-28 md:pt-32">
      <picture>
        <source
          type="image/avif"
          srcSet={`${hero640Avif} 640w, ${hero960Avif} 960w, ${hero1280Avif} 1280w`}
          sizes="(min-width: 768px) 62vw, 100vw"
        />
        <source
          type="image/webp"
          srcSet={`${hero640Webp} 640w, ${hero960Webp} 960w, ${hero1280Webp} 1280w`}
          sizes="(min-width: 768px) 62vw, 100vw"
        />
        <img
          src={heroImage}
          alt="Civil engineering student holding blueprints at a Bengaluru construction site"
          width={1280}
          height={1024}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-y-0 right-0 h-full w-full object-cover object-right opacity-70 md:w-[62%] md:opacity-100"
        />
      </picture>

      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/40 md:via-navy/80" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-navy to-transparent" />

      <div className="container-tight relative">
        <div className="max-w-xl">
          <Reveal>
            <h1 className="text-4xl font-extrabold leading-[1.08] tracking-tight text-navy-foreground sm:text-5xl lg:text-[3.4rem]">
              Become a
              <span className="mt-1 block text-gold">Civil Engineering</span>
              Campus Ambassador
            </h1>
          </Reveal>

          <Reveal delay={90}>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-navy-foreground/70">
              Help Civil Engineering students get industry-standard CAD &amp; BIM software,
              earn real money on every successful license sale, and build a career while
              you&apos;re still on campus.
            </p>
          </Reveal>

          <Reveal delay={160}>
            <div className="mt-8 inline-flex items-center gap-4 rounded-2xl bg-white/5 p-5 ring-1 ring-white/10 backdrop-blur">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                <Wallet className="h-6 w-6" />
              </span>
              <span>
                <span className="block text-2xl font-extrabold text-navy-foreground">
                  Earn <span className="text-gold">₹500</span>
                </span>
                <span className="block text-sm text-navy-foreground/60">
                  Per Successful License Sale
                </span>
              </span>
            </div>
          </Reveal>

          <Reveal delay={230}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#apply"
                className="group inline-flex items-center gap-3 rounded-xl bg-gold px-7 py-4 text-sm font-extrabold tracking-wide text-gold-foreground shadow-xl shadow-black/25 transition-all duration-200 hover:-translate-y-0.5 hover:brightness-105"
              >
                APPLY NOW
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </a>
              <a
                href="#how-it-works"
                className="text-sm font-semibold text-navy-foreground/70 underline-offset-4 transition-colors hover:text-navy-foreground hover:underline"
              >
                See how it works
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal
          delay={300}
          className="mt-10 max-w-xs md:absolute md:bottom-4 md:left-1/2 md:mt-0"
        >
          <div className="rounded-2xl border border-white/10 bg-navy-soft/80 p-5 shadow-2xl shadow-black/30 backdrop-blur">
            <div className="flex items-start justify-between gap-4">
              <p className="text-sm font-semibold text-navy-foreground">
                Your Earning Potential
              </p>
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400">
                <TrendingUp className="h-4 w-4" />
              </span>
            </div>
            <p className="mt-4 text-xs uppercase tracking-wider text-navy-foreground/50">
              Example earnings
            </p>
            <p className="mt-1 text-3xl font-extrabold text-emerald-400">₹15,000+</p>
            <p className="text-sm font-medium text-navy-foreground/70">Per Month</p>
            <p className="mt-3 text-xs text-navy-foreground/45">Based on 30+ sales</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function BenefitsSection() {
  return (
    <section id="benefits" className="bg-secondary/60 py-16 md:py-20">
      <div className="container-tight">
        <Reveal>
          <h2 className="text-center text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            Why Join?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-sm text-muted-foreground">
            Six concrete reasons ambassadors stay with the program for more than one
            semester.
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {benefits.map((benefit, index) => (
            <Reveal key={benefit.title} delay={index * 60}>
              <article className="group h-full rounded-2xl border border-border/70 bg-card p-5 text-center shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5">
                <span
                  className={`mx-auto flex h-12 w-12 items-center justify-center rounded-xl ${benefit.tone}`}
                >
                  <benefit.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground">
                  {benefit.title}
                </h3>
                {benefit.lines.map((line) => (
                  <p key={line} className="text-xs leading-relaxed text-muted-foreground">
                    {line}
                  </p>
                ))}
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section
      id="how-it-works"
      className="bg-gradient-to-b from-background to-secondary/70 py-16 md:py-20"
    >
      <div className="container-tight">
        <Reveal>
          <h2 className="text-center text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            How It Works
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-sm text-muted-foreground">
            From application to your first payout in five clear steps.
          </p>
        </Reveal>

        <div className="relative mt-12">
          <div className="absolute left-0 right-0 top-10 hidden border-t border-dashed border-border lg:block" />
          <ol className="relative grid gap-8 sm:grid-cols-2 lg:grid-cols-5">
            {steps.map((step, index) => (
              <Reveal key={step.label} delay={index * 80} as="li" className="text-center">
                <span
                  className={`relative mx-auto flex h-20 w-20 items-center justify-center rounded-full border bg-card shadow-sm ${step.tone}`}
                >
                  <step.icon className="h-8 w-8" />
                  <span
                    className={`absolute -bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white ${step.dot}`}
                  >
                    {index + 1}
                  </span>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground">{step.label}</h3>
                <p className="mx-auto mt-1 max-w-[15rem] text-xs leading-relaxed text-muted-foreground">
                  {step.copy}
                </p>
              </Reveal>
            ))}

          </ol>
        </div>
      </div>
    </section>
  );
}

function ApplySection() {
  return (
    <section id="apply" className="bg-secondary/70 pb-16 md:pb-20">
      <div className="container-tight grid items-start gap-6 lg:grid-cols-[1.05fr_1fr]">
        <Reveal>
          <div className="overflow-hidden rounded-3xl bg-navy shadow-2xl shadow-black/20">
            <div className="relative px-7 pb-6 pt-8">
              <picture>
                <source
                  type="image/avif"
                  srcSet={`${bp512Avif} 512w, ${bp768Avif} 768w, ${bp1024Avif} 1024w`}
                  sizes="(min-width: 1024px) 33vw, 66vw"
                />
                <source
                  type="image/webp"
                  srcSet={`${bp512Webp} 512w, ${bp768Webp} 768w, ${bp1024Webp} 1024w`}
                  sizes="(min-width: 1024px) 33vw, 66vw"
                />
                <img
                  src={blueprintImage}
                  alt="Blueprint wireframe of a building"
                  loading="lazy"
                  decoding="async"
                  width={1024}
                  height={768}
                  className="pointer-events-none absolute -right-6 top-0 h-full w-2/3 object-contain opacity-70"
                />
              </picture>

              <div className="relative">
                <h2 className="text-2xl font-extrabold text-navy-foreground md:text-3xl">
                  Who Can Apply?
                </h2>
                <ul className="mt-5 space-y-3">
                  {eligibility.map((item) => (
                    <li
                      key={item}
                      className="flex items-center gap-3 text-sm font-medium text-navy-foreground/85"
                    >
                      <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="mt-5 max-w-xs text-xs leading-relaxed text-navy-foreground/50">
                  Applications are reviewed within 48 hours. Only Civil &amp; Diploma Civil
                  backgrounds are considered for this batch.
                </p>
              </div>
            </div>

            <div className="m-4 rounded-2xl bg-navy-soft/70 p-5 ring-1 ring-white/10">
              <p className="text-center text-sm font-bold text-gold">
                Top Performers Win Exciting Rewards
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                {rewards.map((reward) => (
                  <div
                    key={reward.label}
                    className="rounded-xl bg-white/5 px-3 py-4 text-center ring-1 ring-white/10"
                  >
                    <reward.icon className="mx-auto h-7 w-7 text-navy-foreground/80" />
                    <p className="mt-2 text-xs font-medium text-navy-foreground/70">
                      {reward.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <ApplicationForm />
        </Reveal>
      </div>
    </section>
  );
}

function ApplicationForm() {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    const name = form.fullName.trim();
    const college = form.collegeName.trim();
    const whatsapp = form.whatsapp.trim();
    const email = form.email.trim();

    if (name.length < 3) next.fullName = "Enter your full name";
    else if (name.length > 100) next.fullName = "Name is too long";

    if (college.length < 3) next.collegeName = "Enter your college name";
    else if (college.length > 120) next.collegeName = "College name is too long";

    if (!form.branch) next.branch = "Select your branch";

    if (!form.year) next.year = "Select your year";

    if (!/^[6-9]\d{9}$/.test(whatsapp.replace(/\D/g, "").slice(-10)))
      next.whatsapp = "Enter a valid 10-digit WhatsApp number";

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email) || email.length > 255)
      next.email = "Enter a valid email address";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setServerError(null);
    if (!validate()) return;

    setSaving(true);
    const { error } = await supabase.from("applications").insert({
      full_name: form.fullName.trim(),
      college_name: form.collegeName.trim(),
      branch: form.branch,
      year: form.year,
      whatsapp: form.whatsapp.replace(/\D/g, "").slice(-10),
      email: form.email.trim().toLowerCase(),
    });
    setSaving(false);

    if (error) {
      setServerError("We couldn't submit your application. Please try again.");
      return;
    }
    setSubmitted(true);
  };


  if (submitted) {
    return (
      <div className="rounded-3xl border border-border/70 bg-card p-8 text-center shadow-xl shadow-black/5">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
          <BadgeCheck className="h-7 w-7" />
        </span>
        <h2 className="mt-5 text-2xl font-extrabold text-foreground">
          Application received
        </h2>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
          Thanks {form.fullName.split(" ")[0]}. Our campus team will review your profile
          and reach out on WhatsApp within 48 hours with the next steps and your training
          slot.
        </p>
        <button
          type="button"
          onClick={() => {
            setForm(emptyForm);
            setSubmitted(false);
          }}
          className="mt-6 text-sm font-semibold text-primary underline-offset-4 hover:underline"
        >
          Submit another application
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-3xl border border-border/70 bg-card p-6 shadow-xl shadow-black/5 md:p-8"
    >
      <h2 className="text-center text-2xl font-extrabold text-foreground md:text-3xl">
        Apply Now
      </h2>
      <p className="mt-2 text-center text-sm text-muted-foreground">
        Takes under 2 minutes. No fees, ever.
      </p>

      <div className="mt-6 space-y-3.5">
        <Field
          icon={<UserCheck className="h-4 w-4" />}
          error={errors.fullName}
        >
          <input
            type="text"
            value={form.fullName}
            onChange={(event) => update("fullName", event.target.value)}
            placeholder="Full Name"
            maxLength={100}
            aria-label="Full name"
            className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
          />
        </Field>

        <Field
          icon={<Building2 className="h-4 w-4" />}
          error={errors.collegeName}
        >
          <input
            type="text"
            value={form.collegeName}
            onChange={(event) => update("collegeName", event.target.value)}
            placeholder="College Name"
            maxLength={120}
            aria-label="College name"
            className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
          />
        </Field>

        <Field icon={<HardHat className="h-4 w-4" />} error={errors.branch}>
          <div className="relative w-full">
            <select
              value={form.branch}
              onChange={(event) => update("branch", event.target.value)}
              aria-label="Branch"
              className={`w-full appearance-none bg-transparent pr-6 text-sm outline-none ${
                form.branch ? "text-foreground" : "text-muted-foreground/70"
              }`}
            >
              <option value="">Select Branch</option>
              {CIVIL_BRANCHES.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </Field>

        <Field icon={<GraduationCap className="h-4 w-4" />} error={errors.year}>
          <div className="relative w-full">
            <select
              value={form.year}
              onChange={(event) => update("year", event.target.value)}
              aria-label="Year or semester"
              className={`w-full appearance-none bg-transparent text-sm outline-none ${
                form.year ? "text-foreground" : "text-muted-foreground/70"
              }`}
            >
              <option value="">Year / Semester</option>
              <option value="1st Year">1st Year</option>
              <option value="2nd Year">2nd Year</option>
              <option value="3rd Year">3rd Year</option>
              <option value="Final Year">Final Year</option>
              <option value="Diploma">Diploma</option>
              <option value="Fresh Graduate">Fresh Graduate</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </div>
        </Field>

        <Field icon={<Phone className="h-4 w-4" />} error={errors.whatsapp}>
          <span className="text-sm text-muted-foreground">+91</span>
          <input
            type="tel"
            inputMode="numeric"
            value={form.whatsapp}
            onChange={(event) => update("whatsapp", event.target.value)}
            placeholder="WhatsApp Number"
            maxLength={14}
            aria-label="WhatsApp number"
            className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
          />
        </Field>

        <Field icon={<Mail className="h-4 w-4" />} error={errors.email}>
          <input
            type="email"
            value={form.email}
            onChange={(event) => update("email", event.target.value)}
            placeholder="Email Address"
            maxLength={255}
            aria-label="Email address"
            className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
          />
        </Field>
      </div>

      {serverError ? (
        <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-center text-xs font-medium text-destructive">
          {serverError}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={saving}
        className="group mt-6 flex w-full items-center justify-center gap-3 rounded-xl bg-gold px-6 py-4 text-sm font-extrabold tracking-wide text-gold-foreground shadow-lg shadow-black/10 transition-all duration-200 hover:-translate-y-0.5 hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {saving ? "SUBMITTING…" : "SUBMIT APPLICATION"}
        <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
      </button>


      <p className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
        Your details stay private. We only contact shortlisted students.
      </p>
    </form>
  );
}

function Field({
  icon,
  error,
  children,
}: {
  icon: ReactNode;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div>
      <div
        className={`flex items-center gap-3 rounded-xl border bg-background px-4 py-3.5 transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/15 ${
          error ? "border-destructive" : "border-input"
        }`}
      >
        <span className="text-muted-foreground">{icon}</span>
        {children}
      </div>
      {error ? <p className="mt-1.5 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

function ValueStrip() {
  return (
    <section className="bg-navy py-14">
      <div className="container-tight grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {valueProps.map((item, index) => (
          <Reveal key={item.title} delay={index * 70}>
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/5 text-gold ring-1 ring-white/10">
                <item.icon className="h-5 w-5" />
              </span>
              <div>
                <h3 className="text-sm font-bold text-navy-foreground">{item.title}</h3>
                <p className="mt-1 text-xs leading-relaxed text-navy-foreground/60">
                  {item.copy}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section id="faq" className="bg-background py-16 md:py-20">
      <div className="container-tight max-w-3xl">
        <Reveal>
          <h2 className="text-center text-3xl font-extrabold tracking-tight text-foreground md:text-4xl">
            Questions, answered
          </h2>
          <p className="mt-3 text-center text-sm text-muted-foreground">
            Still unsure? Message us on WhatsApp and a campus lead will reply.
          </p>
        </Reveal>

        <Reveal delay={80}>
          <Accordion type="single" collapsible className="mt-8 space-y-3">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={faq.q}
                value={`faq-${index}`}
                className="rounded-2xl border border-border/70 bg-card px-5"
              >
                <AccordionTrigger className="text-left text-sm font-semibold text-foreground hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="bg-navy pb-16 pt-4">
      <div className="container-tight text-center">
        <Reveal>
          <span className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-2 text-xs font-semibold tracking-wide text-gold ring-1 ring-white/10">
            <Sparkles className="h-3.5 w-3.5" />
            Bengaluru batch closing soon
          </span>
          <h2 className="mx-auto mt-5 max-w-2xl text-3xl font-extrabold leading-tight text-navy-foreground md:text-4xl">
            Be a leader. Make an impact.
            <span className="block">
              Start your <span className="text-gold">journey</span> today.
            </span>
          </h2>
          <a
            href="#apply"
            className="group mt-8 inline-flex items-center gap-3 rounded-xl bg-gold px-8 py-4 text-sm font-extrabold tracking-wide text-gold-foreground shadow-xl shadow-black/25 transition-all duration-200 hover:-translate-y-0.5 hover:brightness-105"
          >
            APPLY NOW
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="bg-navy-soft pt-14 pb-8">
      <div className="container-tight">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          <div>
            <BrandMark />
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-navy-foreground/60">
              Campus Ambassador Program for Civil Engineering students in Bengaluru —
              learn industry CAD & BIM tools, earn ₹500 per license sale and build a
              career-ready profile.
            </p>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-gold">
              Program
            </p>
            <ul className="mt-4 space-y-3 text-sm text-navy-foreground/65">
              <li>
                <a href="#benefits" className="transition-colors hover:text-gold">
                  Why Join
                </a>
              </li>
              <li>
                <a href="#how" className="transition-colors hover:text-gold">
                  How It Works
                </a>
              </li>
              <li>
                <a href="#apply" className="transition-colors hover:text-gold">
                  Apply Now
                </a>
              </li>
              <li>
                <a href="#faq" className="transition-colors hover:text-gold">
                  FAQ
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-gold">
              Legal
            </p>
            <ul className="mt-4 space-y-3 text-sm text-navy-foreground/65">
              <li>
                <Link to="/terms" className="transition-colors hover:text-gold">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="transition-colors hover:text-gold">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/contact" className="transition-colors hover:text-gold">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-gold">
              Get in touch
            </p>
            <ul className="mt-4 space-y-3 text-sm text-navy-foreground/65">
              <li>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="inline-flex items-center gap-2 break-all transition-colors hover:text-gold"
                >
                  <Mail className="h-4 w-4 shrink-0 text-gold" />
                  {CONTACT_EMAIL}
                </a>
              </li>
              <li className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-gold" />
                Bengaluru, Karnataka, India
              </li>
              <li className="inline-flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0 text-gold" />
                Mon–Sat · 10 AM – 7 PM IST
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-navy-foreground/10 pt-6 text-center md:flex-row md:text-left">
          <p className="text-xs text-navy-foreground/50">
            © {new Date().getFullYear()} DesignFuture · Bengaluru, Karnataka · Campus
            Ambassador Program
          </p>
          <div className="flex items-center gap-5 text-xs text-navy-foreground/50">
            <Link to="/terms" className="transition-colors hover:text-gold">
              Terms
            </Link>
            <Link to="/privacy" className="transition-colors hover:text-gold">
              Privacy
            </Link>
            <Link to="/contact" className="transition-colors hover:text-gold">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

